import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo_full_white.svg';
import { useApi } from '../../contexts/ApiContext';
import './Navbar.scss';

const Navbar: React.FC = () => {
  const api = useApi();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loginError, setLoginError] = useState("");
  const [workspaceHelp, setWorkspaceHelp] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const currentPage = new URLSearchParams(location.search).get('page');
  const isMinimalMode = currentPage === 'wf' || currentPage === 'weatherinport' || currentPage === 'bunker';

  const appBaseUrl = () => {
    const h = window.location.hostname;
    return (h.includes('sandbox') || h.includes('local'))
      ? 'https://app.sandbox.voycalc.com'
      : 'https://app.voycalc.com';
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Hand the email over to the destination's login; it prefills + focuses password.
  const goToLogin = (base: string) => {
    window.location.href = `${base}/?email=${encodeURIComponent(email.trim())}`;
  };

  const handleLoginClick = () => {
    setLoginError("");
    setWorkspaceHelp(false);
    setShowLoginModal(true);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleProLogin = () => {
    if (!emailValid) { setLoginError("Please enter a valid email address."); return; }
    goToLogin(appBaseUrl());
  };

  const handleEnterpriseLogin = async () => {
    if (!emailValid) { setLoginError("Please enter a valid email address."); return; }
    setLoginError("");
    setWorkspaceHelp(false);
    setDiscovering(true);
    try {
      const res = await api.post('discover-tenant', { email: email.trim() }, { api: 'contact' });
      if (res && res.found && res.tenant && res.tenant.url) {
        goToLogin(res.tenant.url);
        return;
      }
      setWorkspaceHelp(true);
    } catch {
      setLoginError("Couldn't look up your workspace right now. Please try again.");
    } finally {
      setDiscovering(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (menuOpen) setMenuOpen(false);
        if (showLoginModal) setShowLoginModal(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, showLoginModal]);

  useEffect(() => {
    if (showLoginModal && emailRef.current) {
      emailRef.current.focus();
    }
  }, [showLoginModal]);

  return (
    <nav className="custom-navbar">
      <div className="container">
        <div className="d-flex align-items-center justify-content-between w-100 position-relative">
          <Link className="navbar-brand" to="/">
            <img src={logo} alt="Logo" width="120" height="80" className="logo" />
          </Link>
          {!isMinimalMode && (
            <div
              className={`collapse navbar-collapse navbar-menu ${menuOpen ? 'show' : ''}`}
              id="navbarNav"
            >
              {/* Mobile menu */}
              <ul className="navbar-links d-lg-none">
                <li className="nav-item">
                  <Link className={`nav-link ${currentPage === 'product' ? 'active' : ''}`} to="/?page=product" onClick={() => setMenuOpen(false)}>
                    Product
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${currentPage === 'about' ? 'active' : ''}`} to="/?page=about" onClick={() => setMenuOpen(false)}>
                    About
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${currentPage === 'price' ? 'active' : ''}`} to="/?page=price" onClick={() => setMenuOpen(false)}>
                    Price
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${currentPage === 'vmsservices' ? 'active' : ''}`} to="/?page=vmsservices" onClick={() => setMenuOpen(false)}>
                    VMS services
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Desktop centered links - absolutely centered irrespective of sides */}
          {!isMinimalMode && (
            <ul className="d-none d-lg-flex position-absolute start-50 top-50 translate-middle m-0 p-0 list-unstyled gap-4">
              <li className="nav-item">
                <Link className={`nav-link ${currentPage === 'product' ? 'active' : ''}`} to="/?page=product" onClick={() => setMenuOpen(false)}>
                  Product
                </Link>
              </li>
              <li className="nav-item">
                <Link className={`nav-link ${currentPage === 'about' ? 'active' : ''}`} to="/?page=about" onClick={() => setMenuOpen(false)}>
                  About
                </Link>
              </li>
              <li className="nav-item">
                <Link className={`nav-link ${currentPage === 'price' ? 'active' : ''}`} to="/?page=price" onClick={() => setMenuOpen(false)}>
                  Price
                </Link>
              </li>
              <li className="nav-item">
                <Link className={`nav-link ${currentPage === 'vmsservices' ? 'active' : ''}`} to="/?page=vmsservices" onClick={() => setMenuOpen(false)}>
                  VMS services
                </Link>
              </li>
            </ul>
          )}

          {!isMinimalMode && (
            <div className="navbar-actions">
              <button
                className="navbar-toggler custom-toggler d-lg-none me-3"
                type="button"
                aria-controls="navbarNav"
                aria-expanded={menuOpen}
                aria-label="Toggle navigation"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              <button className="login-button " type="button" onClick={handleLoginClick}>
                Log In
              </button>
            </div>
          )}
        </div>
      </div>
      {showLoginModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button onClick={handleCloseModal} className="modal-close" aria-label="Close">
              &times;
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Log in</h2>
            <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
              Enter your email, then choose your plan.
            </p>
            <form className="modal-form" onSubmit={(e) => { e.preventDefault(); handleProLogin(); }}>
              <div className="modal-form-group">
                <label htmlFor="email" className="modal-form-label">
                  Email
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  id="email"
                  name="email"
                  className="modal-form-input"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setWorkspaceHelp(false); }}
                  autoComplete="username"
                />
              </div>
              {loginError && (
                <div className="alert alert-danger" role="alert">
                  {loginError}
                </div>
              )}
              {workspaceHelp && (
                <div className="alert alert-info" role="alert">
                  We couldn't find an enterprise workspace for that email. Go to your company's
                  VoyCalc address — it looks like <strong>https://yourcompany.voycalc.com</strong>.
                  Ask your administrator if you're unsure.
                </div>
              )}
              <div className="plan-choice">
                <button type="submit" className="plan-button" disabled={discovering}>
                  <i className="bi bi-person-fill" aria-hidden="true" />
                  <span className="plan-text">
                    <span className="plan-title">Basic / Pro</span>
                    <span className="plan-sub">Individual account</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="plan-button"
                  disabled={discovering}
                  onClick={handleEnterpriseLogin}
                >
                  <i className="bi bi-building" aria-hidden="true" />
                  <span className="plan-text">
                    <span className="plan-title">Enterprise</span>
                    <span className="plan-sub">{discovering ? 'Searching…' : 'Company workspace'}</span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;